/**
 * SUP-ADMIN-UI-01 — platform-admin bug-report triage surface
 * (`/platform-admin/bug-reports`).
 *
 * Cross-tenant queue: a filterable list of bug reports (status | tenantId |
 * kind | severity | createdFrom/createdTo) with cursor pagination, and a
 * detail drawer for the full report + triage actions. Route-gated to
 * `platform_admin` upstream (see app/router.tsx); the BE service layer is the
 * ultimate gate (403 `permission_denied` to anyone else).
 *
 * Filters live in local state (the cursor resets whenever a filter changes).
 * Pagination is forward/back over the opaque `nextCursor` via a small cursor
 * stack. Per the minimal progress-indication convention there is ONE inline
 * loading/error surface, not per-control spinners.
 */
import { useMemo, useState, type ReactElement } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { PageHeading, compactFieldSx, compactMenuProps } from "@aeriosiq/ui";
import { useBugReportsQuery } from "./hooks.js";
import { BugReportDetailDrawer } from "./BugReportDetailDrawer.js";
import {
  BUG_REPORT_KINDS,
  BUG_REPORT_SEVERITIES,
  BUG_REPORT_STATUSES,
  type BugReportListFilters,
  type BugReportSummary,
} from "./types.js";

const PAGE_SIZE = 25;

/** The subset of filters the user edits (paging cursor is managed separately). */
type EditableFilters = Omit<BugReportListFilters, "cursor" | "limit">;

export function BugReportTriagePage(): ReactElement {
  const [filters, setFilters] = useState<EditableFilters>({});
  // Cursor stack: cursorStack[i] is the cursor that produced page i. Page 0
  // uses no cursor. Pushing nextCursor advances; popping goes back.
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const activeCursor =
    cursorStack.length > 0 ? cursorStack[cursorStack.length - 1] : undefined;

  const queryFilters = useMemo<BugReportListFilters>(
    () => ({
      ...filters,
      limit: PAGE_SIZE,
      ...(activeCursor ? { cursor: activeCursor } : {}),
    }),
    [filters, activeCursor],
  );

  const { data, isPending, isError } = useBugReportsQuery(queryFilters);

  function patchFilter<K extends keyof EditableFilters>(
    key: K,
    value: EditableFilters[K] | undefined,
  ): void {
    setCursorStack([]); // any filter edit resets paging
    setFilters((prev) => {
      // Rebuild without a dynamic `delete`: drop the key, re-add when set.
      const entries = Object.entries(prev).filter(([k]) => k !== key);
      if (value !== undefined && value !== "") {
        entries.push([key, value]);
      }
      return Object.fromEntries(entries);
    });
  }

  return (
    <Box sx={{ maxWidth: 1440, mx: "auto", pb: 6 }}>
      <Box sx={{ mb: 3 }}>
        <PageHeading>Bug reports</PageHeading>
        <Typography variant="body2" color="text.secondary">
          Cross-tenant support triage queue. Platform-staff only.
        </Typography>
      </Box>

      <Stack
        direction="row"
        spacing={1.5}
        sx={{ mb: 2, flexWrap: "wrap", gap: 1.5, alignItems: "flex-end" }}
        role="search"
        aria-label="Bug report filters"
      >
        <SelectFilter
          label="Status"
          value={filters.status ?? ""}
          options={BUG_REPORT_STATUSES}
          onChange={(v) => {
            patchFilter("status", v as BugReportListFilters["status"]);
          }}
        />
        <SelectFilter
          label="Kind"
          value={filters.kind ?? ""}
          options={BUG_REPORT_KINDS}
          onChange={(v) => {
            patchFilter("kind", v as BugReportListFilters["kind"]);
          }}
        />
        <SelectFilter
          label="Severity"
          value={filters.severity ?? ""}
          options={BUG_REPORT_SEVERITIES}
          onChange={(v) => {
            patchFilter("severity", v as BugReportListFilters["severity"]);
          }}
        />
        <TextField
          label="Tenant id"
          size="small"
          value={filters.tenantId ?? ""}
          onChange={(e) => {
            patchFilter("tenantId", e.target.value);
          }}
          sx={{ ...compactFieldSx, width: 240 }}
        />
        <TextField
          label="Created from"
          type="date"
          size="small"
          value={dateInput(filters.createdFrom)}
          onChange={(e) => {
            patchFilter("createdFrom", isoFromDate(e.target.value));
          }}
          slotProps={{ inputLabel: { shrink: true } }}
          sx={compactFieldSx}
        />
        <TextField
          label="Created to"
          type="date"
          size="small"
          value={dateInput(filters.createdTo)}
          onChange={(e) => {
            patchFilter("createdTo", isoFromDate(e.target.value));
          }}
          slotProps={{ inputLabel: { shrink: true } }}
          sx={compactFieldSx}
        />
      </Stack>

      {isPending ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Typography color="error">Couldn&apos;t load bug reports.</Typography>
      ) : (
        <>
          <ReportsGrid
            rows={data.items}
            onOpen={(id) => {
              setSelectedId(id);
            }}
          />
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mt: 2 }}
          >
            <Button
              size="small"
              disabled={cursorStack.length === 0}
              onClick={() => {
                setCursorStack((s) => s.slice(0, -1));
              }}
            >
              Previous
            </Button>
            <Button
              size="small"
              disabled={data.nextCursor == null}
              onClick={() => {
                const cursor = data.nextCursor;
                if (cursor != null) {
                  setCursorStack((s) => [...s, cursor]);
                }
              }}
            >
              Next
            </Button>
          </Stack>
        </>
      )}

      <BugReportDetailDrawer
        bugReportId={selectedId}
        onClose={() => {
          setSelectedId(null);
        }}
      />
    </Box>
  );
}

interface SelectFilterProps {
  label: string;
  value: string;
  options: readonly string[];
  onChange: (value: string) => void;
}

function SelectFilter({
  label,
  value,
  options,
  onChange,
}: SelectFilterProps): ReactElement {
  return (
    <TextField
      select
      label={label}
      size="small"
      value={value}
      onChange={(e) => {
        onChange(e.target.value);
      }}
      sx={{ ...compactFieldSx, minWidth: 150 }}
      slotProps={{
        select: { MenuProps: compactMenuProps, displayEmpty: true },
      }}
    >
      <MenuItem value="">
        <em>Any</em>
      </MenuItem>
      {options.map((o) => (
        <MenuItem key={o} value={o}>
          {o}
        </MenuItem>
      ))}
    </TextField>
  );
}

function ReportsGrid({
  rows,
  onOpen,
}: {
  rows: BugReportSummary[];
  onOpen: (id: string) => void;
}): ReactElement {
  if (rows.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 6, color: "text.secondary" }}>
        <Typography variant="h6" color="text.primary" sx={{ mb: 1 }}>
          No bug reports match
        </Typography>
        <Typography variant="body2">Try clearing your filters.</Typography>
      </Box>
    );
  }
  return (
    <Box
      component="table"
      role="grid"
      aria-label="Bug reports"
      sx={{
        width: "100%",
        borderCollapse: "collapse",
        "& th, & td": {
          textAlign: "left",
          py: 1.25,
          px: 1.5,
          borderBottom: (t) => `1px solid ${t.palette.divider}`,
          fontSize: 14,
        },
        "& th": {
          color: "text.secondary",
          fontWeight: 500,
          fontSize: 12,
          textTransform: "uppercase",
          letterSpacing: 0.5,
        },
        "& tr[role='row']": {
          cursor: "pointer",
          transition: "background-color 120ms",
        },
        "& tr[role='row']:hover": {
          bgcolor: (t) =>
            t.palette.mode === "dark"
              ? "rgba(255,255,255,0.04)"
              : "rgba(0,0,0,0.02)",
        },
      }}
    >
      <thead>
        <tr role="row">
          <th role="columnheader" scope="col">
            Title
          </th>
          <th role="columnheader" scope="col">
            Kind
          </th>
          <th role="columnheader" scope="col">
            Severity
          </th>
          <th role="columnheader" scope="col">
            Status
          </th>
          <th role="columnheader" scope="col">
            Source
          </th>
          <th role="columnheader" scope="col">
            Tenant
          </th>
          <th role="columnheader" scope="col">
            Reporter
          </th>
          <th role="columnheader" scope="col">
            Assignee
          </th>
          <th role="columnheader" scope="col">
            Created
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => {
          const rowName = `${r.title} ${r.kind} ${r.severity} ${r.status}`;
          return (
            <tr
              key={r.bugReportId}
              role="row"
              aria-label={rowName}
              tabIndex={0}
              data-bug-report-id={r.bugReportId}
              onClick={() => {
                onOpen(r.bugReportId);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onOpen(r.bugReportId);
                }
              }}
            >
              <td>
                <Typography variant="body2" fontWeight={500}>
                  {r.title}
                </Typography>
              </td>
              <td>{r.kind}</td>
              <td>
                <Chip size="small" label={r.severity} variant="outlined" />
              </td>
              <td>
                <Chip size="small" label={r.status} color="info" />
              </td>
              <td>{r.sourceApp}</td>
              <td>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontFamily: "monospace" }}
                >
                  {r.tenantId}
                </Typography>
              </td>
              <td>{r.reporterEmail ?? "—"}</td>
              <td>{r.assigneeUserId ?? "—"}</td>
              <td>{new Date(r.createdAt).toLocaleDateString()}</td>
            </tr>
          );
        })}
      </tbody>
    </Box>
  );
}

/** ISO instant → `yyyy-mm-dd` for the date input, or "" when unset. */
function dateInput(iso: string | undefined): string {
  if (!iso) return "";
  return iso.slice(0, 10);
}

/** `yyyy-mm-dd` → UTC ISO instant (start of day), or undefined when cleared. */
function isoFromDate(value: string): string | undefined {
  if (!value) return undefined;
  return new Date(`${value}T00:00:00.000Z`).toISOString();
}
